import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, FileText, Scale, ShieldCheck, CreditCard, Package, AlertCircle, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "December 2024";

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="text-gray-400 hover:text-white"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
          <div className="text-blue-500 font-bold text-xl">ShopNFCTags</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <FileText className="w-16 h-16 mx-auto mb-6 text-blue-500" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-400">
            Last Updated: {lastUpdated}
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-12">
          <p className="text-gray-300 leading-relaxed mb-6">
            Welcome to ShopNFCTags. These Terms of Service ("Terms") govern your use of our website and the purchase of our NFC products. By accessing our website or making a purchase, you agree to be bound by these Terms.
          </p>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
            <p className="text-gray-300 leading-relaxed">
              <strong className="text-white">Important:</strong> Please read these Terms carefully before using our services. If you do not agree with any part of these Terms, you must not use our website or purchase our products.
            </p>
          </div>
        </section>

        {/* Acceptance of Terms */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Scale className="w-8 h-8 text-blue-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Acceptance of Terms
            </h2>
          </div>
          <p className="text-gray-400 leading-relaxed mb-4">
            By accessing and using ShopNFCTags.com, you accept and agree to be bound by these Terms and our Privacy Policy. These Terms apply to all visitors, users, and customers of our website and services.
          </p>
          <p className="text-gray-400 leading-relaxed">
            If you are using our services on behalf of a business or organization, you represent that you have the authority to bind that entity to these Terms.
          </p>
        </section>

        {/* Use of Website */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Use of Website and Services
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Permitted Use</h3>
              <p className="text-gray-400 leading-relaxed mb-3">
                You may use our website for lawful purposes only. You agree not to:
              </p>
              <ul className="space-y-2 text-gray-400 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Use our website in any way that violates applicable laws or regulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Engage in any conduct that restricts or inhibits anyone's use of the website</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Attempt to gain unauthorized access to any portion of the website or related systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Use automated systems (bots, scrapers) without our written permission</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Reproduce, duplicate, or exploit any portion of the website for commercial purposes</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Account Responsibilities</h3>
              <p className="text-gray-400 leading-relaxed">
                If you create an account on our website, you are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </div>
          </div>
        </section>

        {/* Intellectual Property */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-8 h-8 text-cyan-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Intellectual Property Rights
            </h2>
          </div>
          <p className="text-gray-400 leading-relaxed mb-4">
            All content on ShopNFCTags.com, including but not limited to text, graphics, logos, images, product designs, and software, is the property of ShopNFCTags or its content suppliers and is protected by copyright, trademark, and other intellectual property laws.
          </p>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <p className="text-gray-300 leading-relaxed">
                You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any of our content without our express written permission. Custom NFC designs created by you remain your intellectual property, but you grant us a license to use them solely for fulfilling your order.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Products and Pricing */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Package className="w-8 h-8 text-orange-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Products, Pricing, and Availability
            </h2>
          </div>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Product Descriptions</h3>
              <p className="text-gray-400 leading-relaxed">
                We strive to provide accurate product descriptions and images. However, we do not warrant that product descriptions, colors, or other content on our website are completely accurate, current, or error-free. Product specifications are subject to change without notice.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Pricing</h3>
              <p className="text-gray-400 leading-relaxed mb-3">
                All prices are listed in USD and are subject to change without notice. We reserve the right to:
              </p>
              <ul className="space-y-2 text-gray-400 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Modify prices at any time before accepting an order</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Correct pricing errors, even after an order has been placed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Cancel orders placed at incorrect prices</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Availability</h3>
              <p className="text-gray-400 leading-relaxed">
                All products are subject to availability. We reserve the right to limit quantities or discontinue any product at any time. If a product becomes unavailable after you place an order, we will notify you and offer a refund or alternative product.
              </p>
            </div>
          </div>
        </section>

        {/* Payment Terms */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="w-8 h-8 text-green-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Payment Terms
            </h2>
          </div>
          <p className="text-gray-400 leading-relaxed mb-4">
            Payment is required at the time of purchase. We accept major credit cards and other payment methods as displayed at checkout. By providing payment information, you represent and warrant that:
          </p>
          <ul className="space-y-2 text-gray-400 ml-6 mb-4">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">•</span>
              <span>You have the legal right to use the payment method</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">•</span>
              <span>The information you provide is accurate and complete</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">•</span>
              <span>You will pay all charges incurred at the prices in effect when incurred</span>
            </li>
          </ul>
          <p className="text-gray-400 leading-relaxed">
            All payments are processed securely through our PCI-compliant payment processor. We do not store your full credit card information on our servers.
          </p>
        </section>

        {/* Shipping and Delivery */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Shipping and Delivery
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            We ship to addresses within the United States and internationally. Shipping costs and estimated delivery times are provided at checkout. Please note:
          </p>
          <Card className="bg-zinc-900 border-zinc-800 mb-4">
            <CardContent className="p-6">
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Delivery times are estimates and not guaranteed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Risk of loss and title for products pass to you upon delivery to the carrier</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>You are responsible for providing accurate shipping information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Additional customs fees or duties may apply for international orders</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Returns and Refunds */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Returns and Refunds
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            We want you to be satisfied with your purchase. Our return and refund policy is designed to protect both you and our business. Key points:
          </p>
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 rounded-lg p-6 mb-4">
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-cyan-500 mt-1">•</span>
                <span>Returns accepted within 14 days for defective or damaged items only</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-500 mt-1">•</span>
                <span>Return Authorization Number (RAN) required before returning items</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-500 mt-1">•</span>
                <span>Custom-designed products are non-refundable unless defective</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-500 mt-1">•</span>
                <span>Sale items cannot be refunded</span>
              </li>
            </ul>
          </div>
          <p className="text-gray-400 leading-relaxed">
            For complete details, please review our{' '}
            <button 
              onClick={() => navigate('/refunds-returns')}
              className="text-blue-500 hover:text-blue-400 transition-colors underline"
            >
              Refund and Returns Policy
            </button>.
          </p>
        </section>

        {/* Warranties and Disclaimers */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <AlertCircle className="w-8 h-8 text-yellow-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Warranties and Disclaimers
            </h2>
          </div>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Product Warranties</h3>
              <p className="text-gray-400 leading-relaxed">
                Our NFC products are warranted to be free from defects in materials and workmanship under normal use for a period of 30 days from the date of delivery. This warranty does not cover damage caused by misuse, abuse, or normal wear and tear.
              </p>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-3">Disclaimer of Warranties</h3>
              <p className="text-gray-300 leading-relaxed mb-3">
                EXCEPT AS EXPRESSLY PROVIDED ABOVE, OUR PRODUCTS AND WEBSITE ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              </p>
              <p className="text-gray-300 leading-relaxed text-sm">
                We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that our website will be uninterrupted, error-free, or secure.
              </p>
            </div>
          </div>
        </section>

        {/* Limitation of Liability */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Limitation of Liability
          </h2>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <p className="text-gray-300 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SHOPNFCTAGS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="text-gray-300 leading-relaxed text-sm">
              Our total liability to you for any claim arising out of or relating to these Terms or our products shall not exceed the amount you paid for the product giving rise to the claim, or $100, whichever is less.
            </p>
          </div>
        </section>

        {/* Indemnification */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Indemnification
          </h2>
          <p className="text-gray-400 leading-relaxed">
            You agree to indemnify, defend, and hold harmless ShopNFCTags, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or related to your use of our website, violation of these Terms, or infringement of any third-party rights.
          </p>
        </section>

        {/* Termination */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Termination
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            We reserve the right to terminate or suspend your access to our website and services at any time, without notice, for conduct that we believe:
          </p>
          <ul className="space-y-2 text-gray-400 ml-6">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span>Violates these Terms or our policies</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span>Is harmful to other users, us, or third parties</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">•</span>
              <span>Violates applicable laws or regulations</span>
            </li>
          </ul>
        </section>

        {/* Governing Law */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Governing Law and Dispute Resolution
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Any dispute arising out of or relating to these Terms or our services shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive or other equitable relief in court.
          </p>
        </section>

        {/* Changes to Terms */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Changes to These Terms
          </h2>
          <p className="text-gray-400 leading-relaxed">
            We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of our website after such changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.
          </p>
        </section>

        {/* Severability */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Severability and Waiver
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect. Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
          </p>
        </section>

        {/* Contact Section */}
        <section className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg p-8 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-white" />
          <h3 className="text-2xl font-bold text-white mb-3">
            Questions About These Terms?
          </h3>
          <p className="text-white/90 mb-6">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="space-y-2 text-white mb-6">
            <p className="font-semibold">ShopNFCTags Legal Department</p>
            <p>Email: <a href="mailto:legal@shopnfctags.com" className="underline hover:text-gray-100">legal@shopnfctags.com</a></p>
            <p>General Inquiries: <a href="mailto:orders@shopnfctags.com" className="underline hover:text-gray-100">orders@shopnfctags.com</a></p>
          </div>
          <Button
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
            onClick={() => window.location.href = 'mailto:legal@shopnfctags.com'}
          >
            Contact Legal Team
          </Button>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 ShopNFCTags. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
